<template>
  <LmDialog
    v-model="modalShow"
    :title="`Share per department in ${financialPeriodAbbr}`"
    :styles="{ height: '100%' }"
    full-width
  >
    <template #content>
      <q-card-section class="dialog-content">
        <AgGridWrapper
          ref="agGrid"
          :options="gridOptions"
          :title="selectedDepartment ? selectedDepartment.label : ''"
          :infinite-scroll-request="infiniteScrollRequest"
          :update-rows-count="updateRowsCount"
        >
          <template #headerActions>
            <div class="choose-status text-center">
              <q-chip
                v-for="dep of departments"
                :key="dep.id"
                :selected="dep.value === selectedDepartment.value"
                :outline="dep.value !== selectedDepartment.value"
                color="primary"
                text-color="white"
                clickable
                @click="changeDepartment(dep)"
              >
                {{ dep.label }}
              </q-chip>
            </div>
          </template>

          <template #footerActions>
            <div class="row fit q-pr-md q-pb-md justify-end">Total Rows: {{ totalRows }}</div>
          </template>
        </AgGridWrapper>
      </q-card-section>
    </template>
  </LmDialog>
</template>

<script>
import * as apiTo from '../../../api'
import { dateColumn, renderEndDate } from '@/lib/reusable-ag-grid-columns'
import { dateRenderer, priceWithCurrencyRenderer } from '@/lib/generic.utils'
import { priceColumn } from '@/lib/reusable-ag-grid-columns'
import { formatPrice } from '@/lib/formatting.utils'
// Libraries
import { mapState } from 'vuex'
// Components
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import LmGridEntityLink from '@components/ag-grid-helpers/EntityLink'
import AgGridTextDateFilter from '@components/ag-grid-helpers/AgGridTextDateFilter'
import AgGridPriceFilter from '@components/ag-grid-helpers/AgGridPriceFilter'

export default {
  components: {
    LmDialog,
    AgGridWrapper
  },

  props: {
    value: Boolean,
    periodId: [String, Number],
    departmentName: String,
    departments: Array
  },

  data: () => ({
    selectedPeriodId: null,
    selectedDepartment: null,
    totalRows: null
  }),

  computed: {
    ...mapState('financial-insights', ['financialPeriods']),

    modalShow: {
      get() {
        return this.value
      },

      set(val) {
        this.$emit('input', val)
      }
    },

    financialPeriodAbbr() {
      if (!this.selectedPeriodId) return
      const period = this.financialPeriods.find(period => period.id === this.selectedPeriodId)
      return period.abbreviation
    },

    gridOptions() {
      return {
        columnDefs: [
          {
            headerName: 'Subscription Name',
            field: 'subscription.product.name',
            tooltipField: 'subscription.product.name',
            cellRendererParams: params => {
              if (!params.data) return
              if (!params || !params.value) return
              return {
                customData: {
                  id: params.data.subscription.id,
                  routeName: 'subscription',
                  label: params.value,
                  newTab: false
                }
              }
            },
            cellRendererFramework: LmGridEntityLink
          },
          {
            headerName: 'Publisher',
            field: 'publisher.name',
            tooltipValueGetter: params => params.value || 'N/A',
            cellRenderer: params => params.value || 'N/A'
          },
          {
            headerName: 'Vendor',
            field: 'vendor.name',
            tooltipValueGetter: params => params.value || 'N/A',
            cellRenderer: params => params.value || 'N/A'
          },
          dateColumn({
            headerName: 'Start date',
            field: 'start_date',
            valueGetter: params => {
              if (!params.data) return
              return dateRenderer(params.data.start_date)
            },
            cellRenderer: params => dateRenderer(params.data.start_date),
            tooltipValueGetter: params => {
              if (!params.data) return
              return dateRenderer(params.data.start_date)
            }
          }),
          dateColumn({
            headerName: 'End date',
            field: 'end_date',
            cellRenderer: renderEndDate,
            tooltipValueGetter: renderEndDate,
            valueGetter: renderEndDate
          }),
          priceColumn('price', {
            headerName: 'Price in Original currency',
            cellRenderer: params => {
              if (!params.data) return
              params.value = params.data.price
              return priceWithCurrencyRenderer(params, 'currency')
            },
            tooltipValueGetter: params => {
              if (!params.data) return
              params.value = params.data.price
              return priceWithCurrencyRenderer(params, 'currency', true)
            },
            valueGetter: params => {
              if (!params.data) return
              return formatPrice(params.data.price) || ''
            }
          }),
          priceColumn('normalized_price', {
            headerName: 'Price in Local currency',
            cellRenderer: params => {
              if (!params.data) return
              params.value = params.data.normalized_price
              return priceWithCurrencyRenderer(params, 'normalized_currency')
            },
            tooltipValueGetter: params => {
              if (!params.data) return
              params.value = params.data.normalized_price
              return priceWithCurrencyRenderer(params, 'normalized_currency', true)
            },
            valueGetter: params => {
              if (!params.data) return
              return formatPrice(params.data.normalized_price) || ''
            }
          }),
          priceColumn('specific_department_stake.percentage', {
            headerName: 'Department share',
            cellRenderer: params => {
              if (!params.data) return
              return params.data.specific_department_stake.percentage + '%'
            },
            tooltipValueGetter: params => {
              if (!params.data) return
              return params.data.specific_department_stake.percentage + '%'
            },
            valueGetter: params => {
              if (!params.data) return
              return params.data.specific_department_stake.percentage + '%'
            }
          }),
          priceColumn('specific_department_stake.price_share', {
            headerName: 'Original price share',
            cellRenderer: params => {
              if (!params.data) return
              params.value = params.data.specific_department_stake.price_share
              return priceWithCurrencyRenderer(params, 'currency')
            },
            tooltipValueGetter: params => {
              if (!params.data) return
              params.value = params.data.specific_department_stake.price_share
              return priceWithCurrencyRenderer(params, 'currency', true)
            },
            valueGetter: params => {
              if (!params.data) return
              return formatPrice(params.data.specific_department_stake.price_share) || ''
            }
          }),
          priceColumn('specific_department_stake.normalized_price_share', {
            headerName: 'Local price share',
            cellRenderer: params => {
              if (!params.data) return
              params.value = params.data.specific_department_stake.normalized_price_share
              return priceWithCurrencyRenderer(params, 'normalized_currency')
            },
            tooltipValueGetter: params => {
              if (!params.data) return
              params.value = params.data.specific_department_stake.normalized_price_share
              return priceWithCurrencyRenderer(params, 'normalized_currency', true)
            },
            valueGetter: params => {
              if (!params.data) return
              return formatPrice(params.data.specific_department_stake.normalized_price_share) || ''
            }
          })
        ],
        frameworkComponents: {
          AgGridTextDateFilter,
          AgGridPriceFilter
        },
        defaultColDef: {
          floatingFilter: true,
          filter: 'agTextColumnFilter',
          suppressMenu: true,
          floatingFilterComponentParams: {
            debounceMs: 800
          }
        },
        cacheBlockSize: 40,
        pagination: false,
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',
        domLayout: 'normal'
      }
    }
  },

  watch: {
    value(showModal) {
      if (showModal) {
        this.selectedPeriodId = this.periodId
        this.selectedDepartment = {
          ...this.departments.find(d => d.value === this.departmentName)
        }
      }
    }
  },

  methods: {
    infiniteScrollRequest(request) {
      return apiTo.getSharePerDepartment(request, this.selectedPeriodId, this.selectedDepartment.value)
    },

    updateRowsCount(rowsCount) {
      this.totalRows = rowsCount
    },

    changeDepartment(newDep) {
      this.selectedDepartment = newDep
      this.$refs.agGrid.purgeServerSideCache()
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/mixins'

styledScrollFor('>>> .ag-body-viewport.ag-layout-normal');

>>> .q-chip__icon {
  font-size 1rem
  font-weight 300
}

>>> .table-content {
  height calc(100% - 90px)
  padding 0 40px

  .ag-theme-material {
    height  100%
  }

  .ag-center-cols-viewport {
    overflow-x hidden
  }
}

.dialog-content {
  display grid
  gap 15px
  padding 25px
}
</style>
